import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Loader2 } from "lucide-react";
import { AppErrorBoundary } from "@/components/app/ErrorBoundary";

const Index = lazy(() => import("./pages/Index"));
const SetupPage = lazy(() => import("./pages/Setup"));
const CompleteSetupPage = lazy(() => import("./pages/CompleteSetup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AppLayout = lazy(() => import("./layouts/AppLayout"));
const CalendarPage = lazy(() => import("./pages/app/Calendar"));
const StaffPage = lazy(() => import("./pages/app/Staff"));
const MySchedulePage = lazy(() => import("./pages/app/MySchedule"));
const LocationsPage = lazy(() => import("./pages/app/Locations"));
const LocationDetailPage = lazy(() => import("./pages/app/LocationDetail"));
const MyAvailabilityPage = lazy(() => import("./pages/app/MyAvailability"));
const AvailabilityOverviewPage = lazy(() => import("./pages/app/AvailabilityOverview"));
const AccessCodesPage = lazy(() => import("./pages/app/AccessCodes"));
const SettingsPage = lazy(() => import("./pages/app/Settings"));
const WeeklyAuditPage = lazy(() => import("./pages/app/WeeklyAudit"));
const MyAttendancePage = lazy(() => import("./pages/app/MyAttendance"));
const AttendanceOverviewPage = lazy(() => import("./pages/app/AttendanceOverview"));
const MyLeavePage = lazy(() => import("./pages/app/MyLeave"));
const LeaveRequestsPage = lazy(() => import("./pages/app/LeaveRequests"));
const ShiftSwapsPage = lazy(() => import("./pages/app/ShiftSwaps"));
const MySwapsPage = lazy(() => import("./pages/app/MySwaps"));
const CheckInPage = lazy(() => import("./pages/app/CheckIn"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmail"));
const InvitePage = lazy(() => import("./pages/Invite"));
const VerifiedPage = lazy(() => import("./pages/auth/Verified"));
const VerifyErrorPage = lazy(() => import("./pages/auth/VerifyError"));
const DashboardPage = lazy(() => import("./pages/app/Dashboard"));
const MyWeekPage = lazy(() => import("./pages/app/MyWeek"));
const TimeTrackingTimerPage = lazy(() => import("./pages/app/TimeTrackingTimer"));
const TimeTrackingLogHoursPage = lazy(() => import("./pages/app/TimeTrackingLogHours"));
const MyTimesheetsPage = lazy(() => import("./pages/app/MyTimesheets"));
const ManagerTimesheetsPage = lazy(() => import("./pages/app/ManagerTimesheets"));
const StaffTimesheetDetailPage = lazy(() => import("./pages/app/StaffTimesheetDetail"));
const ManagerInsightsPage = lazy(() => import("./pages/app/ManagerInsights"));
const ShiftIncidentsPage = lazy(() => import("./pages/app/ShiftIncidents"));
const TeamAccessPage = lazy(() => import("./pages/app/TeamAccess"));
const DataHealthPage = lazy(() => import("./pages/app/DataHealth"));
const AccountPage = lazy(() => import("./pages/app/Account"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorks"));
const QAPage = lazy(() => import("./pages/app/QA"));
const FloorPlanPage = lazy(() => import("./pages/app/FloorPlan"));
const WhosWorkingPage = lazy(() => import("./pages/app/WhosWorking"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-5 h-5 animate-spin text-primary" />
  </div>
);

const App = () => {
  useEffect(() => {
    // Font size
    const savedSize = localStorage.getItem("tempo_font_size");
    const root = document.documentElement;
    root.classList.remove("font-size-small", "font-size-default", "font-size-large", "font-size-xl");
    if (savedSize && savedSize !== "default") {
      root.classList.add(`font-size-${savedSize}`);
    }

    // Font family
    const savedFont = localStorage.getItem("tempo_font_family") || "inter";
    root.classList.remove("font-inter", "font-nunito", "font-poppins");
    root.classList.add(`font-${savedFont}`);

    // Inject Google Fonts link if not already present
    if (!document.getElementById("tempo-google-fonts")) {
      const link = document.createElement("link");
      link.id = "tempo-google-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Nunito:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const savedFont = localStorage.getItem("tempo_font_family") || "inter";
      const root = document.documentElement;
      root.classList.remove("font-inter", "font-nunito", "font-poppins");
      root.classList.add(`font-${savedFont}`);
    };
    window.addEventListener("fontFamilyChanged", handler);
    return () => window.removeEventListener("fontFamilyChanged", handler);
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/setup" element={<SetupPage />} />
                <Route path="/complete-setup" element={<CompleteSetupPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/invite" element={<InvitePage />} />
                <Route path="/auth/verified" element={<VerifiedPage />} />
                <Route path="/auth/verify-error" element={<VerifyErrorPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/app" element={
                  <AppErrorBoundary>
                    <AppLayout />
                  </AppErrorBoundary>
                }>
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="staff" element={<StaffPage />} />
                  <Route path="my-schedule" element={<MySchedulePage />} />
                  <Route path="my-week" element={<MyWeekPage />} />
                  <Route path="locations" element={<LocationsPage />} />
                  <Route path="locations/:id" element={<LocationDetailPage />} />
                  <Route path="my-availability" element={<MyAvailabilityPage />} />
                  <Route path="availability" element={<AvailabilityOverviewPage />} />
                  <Route path="audit" element={<WeeklyAuditPage />} />
                  <Route path="access-codes" element={<AccessCodesPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="my-attendance" element={<MyAttendancePage />} />
                  <Route path="attendance" element={<AttendanceOverviewPage />} />
                  <Route path="my-leave" element={<MyLeavePage />} />
                  <Route path="leave-requests" element={<LeaveRequestsPage />} />
                  <Route path="swaps" element={<ShiftSwapsPage />} />
                  <Route path="my-swaps" element={<MySwapsPage />} />
                  <Route path="check-in" element={<CheckInPage />} />
                  <Route path="time-tracking/timer" element={<TimeTrackingTimerPage />} />
                  <Route path="time-tracking/log-hours" element={<TimeTrackingLogHoursPage />} />
                  <Route path="time-tracking/my-timesheets" element={<MyTimesheetsPage />} />
                  <Route path="timesheets" element={<ManagerTimesheetsPage />} />
                  <Route path="timesheets/staff/:staffId" element={<StaffTimesheetDetailPage />} />
                  <Route path="insights" element={<ManagerInsightsPage />} />
                  <Route path="incidents" element={<ShiftIncidentsPage />} />
                  <Route path="team-access" element={<TeamAccessPage />} />
                  <Route path="data-health" element={<DataHealthPage />} />
                  <Route path="floor-plan" element={<FloorPlanPage />} />
                  <Route path="whos-working" element={<WhosWorkingPage />} />
                  <Route path="account" element={<AccountPage />} />
                  {(import.meta.env.DEV || import.meta.env.VITE_ENABLE_QA === "true") && (
                    <Route path="qa" element={<QAPage />} />
                  )}
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
